export enum tosPage {
  mappingNetwork = 'mapping-network',
  bee = 'bee',
}

interface NestedItem {
  title: string;
  content?: (string | TrustedHTML)[];
  list?: string[];
  description?: string;
}

export interface ContentSection {
  title: string;
  content?: string[];
  list?: string[];
  nestedItems?: NestedItem[];
}

export type Content = {
  [key in tosPage]: {
    title: string;
    date: string;
    sections: ContentSection[];
  };
};

export type Menu = {
  [key in tosPage]: {
    title: string;
    target: tosPage;
  };
};

export const menu: Menu = {
  [tosPage.bee]: {
    title: 'Bee',
    target: tosPage.bee,
  },
  [tosPage.mappingNetwork]: {
    title: 'Mapping Network',
    target: tosPage.mappingNetwork,
  },
};

export const content: Content = {
  [tosPage.mappingNetwork]: {
    title: 'Mapping Network Terms of Service',
    date: 'May 5, 2026',
    sections: [
      {
        title: 'Summary of Changes',
        content: [
          'This Agreement was updated on May 5, 2026 to reflect how the Hivemapper Network has evolved. In its early years, Devices on the Network were dashcams with limited compute, primarily capturing street-level imagery for inclusion in a central map product. As the technology has matured, Devices now carry significantly more compute and sensors — producing video, location and inertial data, and on-device feature detections, and running developer-authored workloads on-edge.',
          'Customers increasingly want to commission specific data and run their own models against Network coverage, rather than license pre-built data from Hivemapper. To support this, the Network has shifted from a centralized data-collection-and-distribution model to a marketplace in which Hivemapper operates the Network of Devices and developers lease access to those Devices and their compute through Work Orders. This is simpler to operate and scales better as workload variety grows. The transition is reflected in MIP-23 and MIP-26.',
          'Material changes to this Agreement include:',
        ],
        list: [
          'The Network is now a marketplace of sensors and compute. Devices operated by Contributors fulfill Work Orders posted by developers in exchange for HONEY rewards. Map Coverage rewards remain the same.',
          'Data flows directly from Devices to developers. Hivemapper no longer custodies Contributor Data; data delivered through the Network is licensed directly from Contributors to commissioning developers.',
          "Hivemapper's role is protocol operator. Hivemapper operates the Network, validates fulfillment, and distributes rewards, but does not own or license Contributor Data.",
          'Developer obligations. Developers are required to comply with applicable privacy and data-protection laws. Hivemapper may revoke developer access for violations.',
          'Public Network Displays. The Hivemapper map, coverage, and regions views remain available as marketplace-level views, subject to restrictions on scraping or commercial use.',
        ],
      },
      {
        title: 'Introduction',
        content: [
          'These Terms of Service constitute a legally binding agreement (the "Agreement") between you and Hivemapper, Inc., its parents, subsidiaries, representatives, affiliates, officers and directors (collectively, "Hivemapper," "we," "us" or "our") governing your participation in the Hivemapper Network (the "Network") and your use of the Hivemapper website at hivemapper.com (the "Site"), the Hivemapper mobile applications (the "Apps"), devices authorized by Hivemapper for use on the Network (each, a "Device"), and any other software, services, or content made available by Hivemapper (collectively, the "Services").',
          'The Network is a decentralized system in which contributor-operated Devices collect data and execute on-device computation in response to demand by developers. This Agreement governs your role as a Contributor to the Network.',
          'Your use of the Services is subject to this Agreement, which may be updated from time to time. Please read this Agreement carefully and make sure you understand it. If you do not agree, you may not participate in the Network or use the Services.',
        ],
      },
      {
        title: 'Modifications to the Agreement',
        content: [
          'Hivemapper may modify this Agreement from time to time. The Network is governed by an open improvement-proposal process (Map Improvement Proposals, or "MIPs") under which reward mechanics, pricing, workload types, and other Network rules may be amended. By continuing to participate in the Network or use the Services after such updates take effect, you agree to be bound by the revised Agreement and the Network\'s then-current MIPs. Material changes will be communicated via the Site, the Apps, or other reasonable means.',
        ],
      },
      {
        title: 'The Network: Architecture and Data Flow',
        nestedItems: [
          {
            title: 'What the Network Is',
            content: ['The Network is a marketplace in which:'],
            list: [
              'Contributors operate Devices that collect sensor inputs (which may include imagery, video, location data, inertial data, and other sensor outputs) and execute computational workloads on those inputs.',
              'Hivemapper operates the Network protocol, including the work-order system, validation, reward distribution, and Network-level requirements.',
              'Developers post Work Orders specifying geographic coverage and the workloads they wish to have executed. Developers pay for Network access using Map Credits (acquired by burning HONEY).',
            ],
          },
          {
            title: 'Data Flow',
            content: [
              "Outputs produced by workloads on the Network — including raw sensor data and any computational outputs the developer's workload generates — are delivered from the Contributor's Device to the developer who commissioned them under the applicable Work Order. Hivemapper operates the protocol that facilitates this delivery; Hivemapper does not custody, license, or own these outputs.",
            ],
          },
          {
            title: 'Hivemapper Public Network Displays',
            content: [
              'As part of operating the marketplace, Hivemapper maintains certain public-facing displays of Network activity, including (without limitation) the Hivemapper map, coverage view, and regions view (collectively, the "Network Displays"). The Network Displays are made available through the Services for public viewing. They are operated by Hivemapper as protocol-level marketplace views and are not commercial data products that Hivemapper sells or licenses to third parties. Restrictions on use of the Network Displays are set forth in the "Network Displays" section below.',
            ],
          },
          {
            title: 'Developer Obligations',
            content: [
              'Developers are required to comply with applicable laws regarding privacy and fair use of the data generated by devices on the Network, including without limitation data-protection laws, export-control laws, and the rights of individuals captured in collected data.',
              "Hivemapper reserves the right to suspend or revoke a developer's access to the Network, including forfeiture of unspent Map Credits, for violation of these obligations.",
            ],
          },
        ],
      },
      {
        title: 'Eligibility',
        content: [
          "You must be at least 13 years old to participate in the Network. If you are under 18, you represent that you have your parent or guardian's permission to participate, and your parent or guardian must read this Agreement with you. If you are a parent or legal guardian of a Contributor under 18, you are responsible for that Contributor's activity. If you are participating on behalf of a company or organization, you represent that you have authority to bind that entity to this Agreement.",
        ],
      },
      {
        title: 'Contributor Accounts and Rewards',
        nestedItems: [
          {
            title: 'Accounts',
            content: [
              'To participate in the Network you must register an account through the Apps or another authorized means. You are responsible for safeguarding your account credentials and for all activity that occurs under your account.',
            ],
          },
          {
            title: 'HONEY Rewards',
            content: [
              'Contributors may earn rewards in HONEY tokens, including:',
            ],
            list: [
              'Map Coverage Rewards, for collecting and submitting eligible data;',
              "Consumption Rewards, distributed to Contributors when paid developer demand against Work Orders is fulfilled by validated Contributor activity, in accordance with the Network protocol's then-current rules;",
              'Such other reward types as may be defined by the Network protocol from time to time.',
            ],
          },
          {
            title: 'Reward Mechanics',
            content: [
              "The mechanics of all rewards — including pricing, eligibility, validation, and distribution — are governed by the Network protocol's then-current rules, including any active MIPs. Hivemapper does not guarantee that you will earn any HONEY. HONEY may have no value, and Hivemapper makes no promises or warranties as to the value of HONEY.",
              'Only original and authentic data collected at a verifiable location will qualify for rewards. Any falsified, manipulated, or non-original data will not qualify, may result in forfeiture of past rewards, and may result in termination of Network access. Rewards paid for data later found to be in breach of this Agreement are due back to Hivemapper and may be deducted from future rewards.',
            ],
          },
          {
            title: 'Scam Warning',
            content: [
              'You will never need to sign a transaction to receive Hivemapper rewards. Genuine rewards from Hivemapper are always dropped directly into your wallet. If you are ever asked to sign a transaction by an entity claiming to be Hivemapper, it is a scam.',
            ],
          },
          {
            title: 'No Guarantee of Work Orders',
            content: [
              "Whether and how often your Device fulfills Work Orders depends on developer demand, your geographic coverage, your Device's compliance status, and other factors. Hivemapper does not guarantee any minimum volume of Work Orders, rewards, or earnings.",
            ],
          },
        ],
      },
      {
        title: 'Data You Submit; Licenses You Grant',
        nestedItems: [
          {
            title: 'Contributor Data',
            content: [
              '"Contributor Data" means the data your Device collects and submits through the Network in fulfillment of Work Orders, including raw sensor data, on-device processed outputs, and any other data your Device transmits.',
              'You represent and warrant that:',
            ],
            list: [
              'You operate your Device in compliance with applicable law, including privacy and data-protection law;',
              'You have all rights necessary to operate the Device in the locations where you do so;',
              'The Contributor Data you submit is original, collected at the location it claims to be at, and not knowingly manipulated, falsified, or intended to mislead;',
              'The Contributor Data does not infringe third-party rights;',
              'You have not configured your Device to circumvent Network privacy or validation requirements.',
            ],
          },
          {
            title: 'License Grant to Developers',
            content: [
              "By participating in the Network, you grant a worldwide, perpetual, irrevocable, royalty-free license to use, reproduce, process, distribute, and prepare derivative works from your Contributor Data to each developer whose Work Order is fulfilled by your Device, with respect to the Contributor Data delivered to that developer pursuant to that Work Order. The scope of each developer's rights is limited to what their Work Order produced and does not extend to any other developer.",
            ],
          },
          {
            title: 'Limited License to Hivemapper',
            content: [
              'You grant Hivemapper a limited, non-exclusive, royalty-free license to operate the technology, protocol, and systems necessary to facilitate the Network — including Work Order matching, validation of fulfillment, fraud detection, reward distribution, and operation of the Services — and to use metadata about Network activity in operating the Network Displays. Hivemapper does not receive a license to use, copy, distribute, or commercialize Contributor Data itself, and Hivemapper does not own or custody Contributor Data.',
            ],
          },
          {
            title: 'Direct Delivery',
            content: [
              "You acknowledge that when your Device fulfills a Work Order, Contributor Data is delivered directly from your Device (or via the Network protocol) to the commissioning developer's infrastructure. Hivemapper is not a recipient of Contributor Data and does not retain copies, except to the limited extent necessary to operate the protocol.",
            ],
          },
          {
            title: 'Data Removal',
            content: [
              "You may request removal of Contributor Data that has been delivered to a developer by contacting Hivemapper. Hivemapper will facilitate such requests with the receiving developer but is not the executor of removal: removal depends on the developer's cooperation and any applicable legal retention requirements. Hivemapper makes no guarantee that any removal request will be fulfilled.",
            ],
          },
        ],
      },
      {
        title: 'Network Displays',
        content: [
          'The Network Displays described above — including the Hivemapper map, coverage view, regions view, and any other public-facing marketplace views Hivemapper makes available through the Services — are operated by Hivemapper as part of the Services.',
          'The Network Displays are not commercial data products. Hivemapper does not sell, license, or otherwise distribute the Network Displays as a data product to third parties. However, Hivemapper retains rights in the Network Displays as protocol-level marketplace views, and the following restrictions apply:',
        ],
        list: [
          'You may view and search the Network Displays through the Services.',
          'You may not download, export, scrape, copy, mine, redistribute, or commercially exploit the Network Displays.',
          'You may not use automated tools, bots, or other means to extract data from the Network Displays in bulk.',
        ],
      },
      {
        title: 'Privacy',
        content: [
          'Our <a href="/privacy/privacy-policy">Privacy Policy</a> explains what personal information we collect, how we treat your personal data, and how the Network\'s privacy requirements are enforced. By participating in the Network, you acknowledge and agree to the Privacy Policy.',
        ],
      },
      {
        title: 'Acceptable Use; Restrictions',
        content: [
          'You may not, whether yourself or through any other means or person:',
        ],
        list: [
          '(1) operate a Device in violation of applicable law, including privacy, data-protection, traffic, or export law;',
          '(2) configure, modify, or operate a Device to circumvent Network privacy processing, validation requirements, or geographic restrictions;',
          '(3) submit data that is falsified, manipulated, not collected where it claims to be collected, or otherwise designed to defraud the Network or developers;',
          '(4) reverse engineer, decompile, disassemble, or attempt to derive the source code of the Services or any Device firmware, except to the extent expressly permitted by law or by an open-source license;',
          '(5) scrape, copy, redistribute, mine, or commercially exploit the Network Displays;',
          '(6) transmit any virus, worm, Trojan Horse, time bomb, web bug, spyware, or other harmful code through the Services;',
          '(7) interfere with or disrupt the Network, the Services, or the servers or networks that host them;',
          "(8) use the Services or a Device to track the location of any person (or property owned or used by that person) without that person's consent;",
          '(9) transfer or assign your account credentials, even temporarily, to a third party;',
          '(10) impersonate any person or entity, or misrepresent your affiliation with any person or entity;',
          '(11) operate the Services or a Device for any illegal or unauthorized purpose;',
          '(12) infringe or violate any provision of this Agreement.',
        ],
      },
      {
        title: 'Termination',
        content: [
          'You may stop participating in the Network at any time. You can request account deletion through the Apps or by contacting Hivemapper; on receipt, Hivemapper will use reasonable efforts to delete your account and associated personal information, subject to backup retention and the limitations in the "Data Removal" section above.',
          'Hivemapper may suspend or terminate your account or Network access at any time, in its sole discretion, for any breach of this Agreement, for harm to the Network or its participants, or for compliance with law. We will notify you of the reason for termination unless doing so would (a) violate law or law-enforcement direction, (b) compromise an investigation or the integrity of the Services, or (c) cause harm to others.',
          'The "License Grant to Developers," "Limited License to Hivemapper," "Network Displays," "Acceptable Use," "Termination," "Warranties and Limitation of Liability," "Indemnity," and "Miscellaneous" sections survive termination.',
        ],
      },
      {
        title: 'Hivemapper Intellectual Property',
        nestedItems: [
          {
            title: 'Ownership',
            content: [
              'As between you and Hivemapper, Hivemapper owns and retains all right, title and interest in and to the Services, the Network protocol, the Apps, Device firmware, the Network Displays, marketplace and validation systems, the Hivemapper brand, and all related intellectual property. Hivemapper does not own and does not claim rights in Contributor Data. Except for the limited rights expressly granted in this Agreement, you receive no license to Hivemapper intellectual property, whether by implication, estoppel, or otherwise. You are not granted any right to use any Hivemapper trademark, service mark, logo, or trade name.',
            ],
          },
          {
            title: 'Feedback',
            content: [
              'If you provide Hivemapper with suggestions, comments, or other feedback regarding the Services or the Network ("Feedback"), you grant Hivemapper a perpetual, irrevocable, royalty-free, worldwide license to use, disclose, reproduce, license, distribute, and commercially exploit such Feedback without restriction.',
            ],
          },
        ],
      },
      {
        title: 'Warranties and Limitation of Liability',
        nestedItems: [
          {
            title: 'No Warranty',
            content: [
              'EXCEPT AS EXPRESSLY STATED IN THIS AGREEMENT OR AS REQUIRED BY LAW, THE SERVICES AND THE NETWORK ARE PROVIDED "AS IS" AND HIVEMAPPER MAKES NO WARRANTIES ABOUT THE SERVICES, INCLUDING NO WARRANTIES THAT (A) THE SERVICES WILL MEET YOUR REQUIREMENTS, (B) THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, (C) ANY CONTRIBUTOR DATA YOU SUBMIT WILL FULFILL WORK ORDERS OR EARN REWARDS, OR (D) THE VALUE, AVAILABILITY, OR TRANSFERABILITY OF HONEY OR MAP CREDITS.',
            ],
          },
          {
            title: 'Limitation of Liability',
            description:
              'EXCEPT AS REQUIRED BY APPLICABLE LAW, HIVEMAPPER, ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES AND AGENTS WILL NOT BE RESPONSIBLE FOR ANY LOSS OF PROFITS, REVENUES, BUSINESS OPPORTUNITIES, GOODWILL, OR ANTICIPATED SAVINGS; LOSS OR CORRUPTION OF DATA; INDIRECT OR CONSEQUENTIAL LOSS; OR PUNITIVE DAMAGES CAUSED BY:',
            list: [
              'ERRORS, MISTAKES, OR INACCURACIES IN THE SERVICES OR THE NETWORK;',
              'PERSONAL INJURY OR PROPERTY DAMAGE RESULTING FROM YOUR USE OF THE SERVICES OR A DEVICE;',
              'ANY UNAUTHORIZED ACCESS TO OR USE OF THE SERVICES OR A DEVICE;',
              'ANY INTERRUPTION OR CESSATION OF THE SERVICES OR THE NETWORK;',
              'ANY VIRUSES OR MALICIOUS CODE TRANSMITTED TO OR THROUGH THE SERVICES BY ANY THIRD PARTY;',
              'ANY ACT, OMISSION, ERROR, OR NEGLIGENCE OF ANY THIRD PARTIES, INCLUDING DEVELOPERS OPERATING ON THE NETWORK;',
              'ANY FAILURE OF A WORK ORDER TO PRODUCE REWARDS, OR ANY DISPUTE BETWEEN YOU AND A DEVELOPER;',
              'THE REMOVAL OR UNAVAILABILITY OF ANY CONTRIBUTOR DATA OR NETWORK DISPLAYS;',
              'ANY CHANGE IN THE VALUE, AVAILABILITY, OR FUNCTIONALITY OF HONEY OR MAP CREDITS, INCLUDING CHANGES MADE PURSUANT TO ANY MIP.',
            ],
            content: [
              'THIS PROVISION APPLIES TO ANY CLAIM, REGARDLESS OF WHETHER THE CLAIM ASSERTED IS BASED ON WARRANTY, CONTRACT, TORT, OR ANY OTHER LEGAL THEORY.',
              "HIVEMAPPER AND ITS AFFILIATES' TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATING TO THE SERVICES IS LIMITED TO THE GREATER OF: (A) THE AMOUNT OF REVENUE THAT HIVEMAPPER HAS PAID TO YOU FROM YOUR USE OF THE SERVICES IN THE 12 MONTHS BEFORE THE DATE OF YOUR WRITTEN NOTICE OF THE CLAIM; AND (B) USD $500.",
            ],
          },
        ],
      },
      {
        title: 'Indemnity',
        content: [
          "To the extent permitted by applicable law, you agree to defend, indemnify, and hold harmless Hivemapper, its affiliates, officers, directors, employees, and agents from and against any and all claims, damages, obligations, losses, liabilities, costs, or expenses (including reasonable attorneys' fees) arising from: (i) your participation in the Network or use of the Services or any Device; (ii) your violation of any term of this Agreement; (iii) your violation of any third-party right, including any copyright, property, or privacy right; or (iv) any claim that your Contributor Data or your Device operation caused damage to a third party. This obligation survives termination of this Agreement.",
        ],
      },
      {
        title: 'Miscellaneous',
        nestedItems: [
          {
            title: 'Export Control',
            content: [
              'You represent and warrant that: (i) you are not located in a country that is subject to a U.S. Government embargo or that has been designated by the U.S. Government as a "terrorist supporting" country; and (ii) you are not listed on any U.S. Government list of prohibited or restricted parties.',
            ],
          },
          {
            title: 'Third-Party Services',
            content: [
              'The Services may reference or interoperate with third-party online services, including developer infrastructure to which Contributor Data is delivered. Hivemapper has no control over and assumes no responsibility for such third-party services. You should review the terms and privacy policy of any third-party service you interact with.',
            ],
          },
          {
            title: 'Modifications to the Services',
            content: [
              'Hivemapper may at any time modify, adapt, or change the Services, the Network protocol, the Site, the Apps, or the availability or functionality of any of them. You will have no claim against Hivemapper for such changes, including changes made pursuant to MIPs.',
            ],
          },
          {
            title: 'Governing Law; Venue',
            content: [
              'All claims arising out of or relating to this Agreement or the Services will be governed by California law, without regard to conflict-of-law rules, and will be litigated exclusively in the federal or state courts of San Francisco County, California, USA. You and Hivemapper consent to personal jurisdiction in those courts.',
            ],
          },
          {
            title: 'Assignment',
            content: [
              "You may not assign or transfer your rights under this Agreement without Hivemapper's prior written consent. Hivemapper may assign its rights and obligations to a third party at its sole discretion, provided such third party undertakes Hivemapper's obligations under this Agreement.",
            ],
          },
          {
            title: 'Force Majeure',
            content: [
              'Hivemapper is relieved from its obligations under this Agreement to the extent and for the duration that performance is prevented by severe weather, natural disasters, acts of God, riots, wars, theft, governmental action, acts of suppliers, or other events beyond our reasonable control.',
            ],
          },
          {
            title: 'Entire Agreement',
            content: [
              'This Agreement, together with the Privacy Policy and any other documents referenced herein, constitutes the entire agreement between you and Hivemapper regarding your role as a Contributor to the Network and supersedes all prior agreements on the same subject matter.',
            ],
          },
          {
            title: 'Severability',
            content: [
              'If any provision of this Agreement is found unenforceable, the remaining provisions will remain in full force and effect.',
            ],
          },
        ],
      },
      {
        title: 'Relationship with Hivemapper',
        content: [
          'As a Contributor to the Network, you acknowledge that you and Hivemapper are independent contracting parties. This Agreement does not create an employment, joint venture, franchise, or agency relationship. You have no authority to bind Hivemapper, and you may not hold yourself out as an employee, agent, or authorized representative of Hivemapper.',
        ],
      },
      {
        title: 'Contact',
        content: [
          'You may contact us with any question about the Services through email at <a href="mailto:hi@hivemapper.com">hi@hivemapper.com.</a> We will make our best efforts to address your inquiry promptly.',
        ],
      },
    ],
  },
  [tosPage.bee]: {
    title: 'Bee Terms of Service',
    date: 'May 5, 2026',
    sections: [
      {
        title: 'Overview',
        content: [
          'The Bee is a Hivemapper Network compliant device designed and built by Bee Maps. By using the Bee on the Hivemapper Network, you are bound by the terms of service which can be found <a href="https://beemaps.com/tos/bee">here</a>.',
        ],
      },
    ],
  },
};

export const normalizeForAnchor = (str: string) => {
  return str
    .toLowerCase()
    .replace(/ /g, '-')
    .replace(/[^a-z0-9-]/g, '');
};
